import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      nav("/admin");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080C14] bg-grid flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link
          to="/"
          data-testid="login-back-home"
          className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] text-slate-500 hover:text-amber-400 transition mb-8"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to portal
        </Link>

        <div className="rounded-2xl bg-[#0E1526]/85 backdrop-blur-xl border border-slate-800 shadow-xl shadow-black/40 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <Truck className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <div className="font-display font-extrabold text-xl text-white">Admin Portal</div>
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
                FGB Dispatch Ops
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-300 text-xs font-mono uppercase tracking-wider">
                Admin Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-username-input"
                className="mt-1.5 bg-[#131C31] border-slate-700 text-white focus-visible:ring-amber-500"
                placeholder="support@fgbdispatching.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300 text-xs font-mono uppercase tracking-wider">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="mt-1.5 bg-[#131C31] border-slate-700 text-white focus-visible:ring-amber-500"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-btn"
              className="w-full bg-amber-500 text-black hover:bg-amber-400 font-semibold"
            >
              {loading ? "Signing in…" : "Sign in to Dispatch Console"}
            </Button>
          </form>

          <p className="text-xs text-slate-500 mt-6 leading-relaxed">
            Restricted access. FGB Dispatching operations personnel only. All activity is logged.
          </p>
        </div>
      </div>
    </div>
  );
}
